import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { serverUnreachable } from "../lib/reach.ts";

/** Once the retries are spent, how often to look again; only while the server stays out of reach. */
const RETRY_EVERY_MS = 20_000;

/**
 * Said at the top of whatever screen is open when its reads cannot reach the
 * server, instead of a page that stays empty with no reason given — usually
 * Tailscale not up yet on the phone. Tries again on its own, and on a tap.
 */
export function Unreachable() {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  const unreachable = useSyncExternalStore(
    useCallback((onChange) => cache.subscribe(onChange), [cache]),
    () => serverUnreachable(cache.findAll({ type: "active" }).map((query) => query.state)),
  );

  const retry = useCallback(
    () => void queryClient.refetchQueries({ type: "active", predicate: (query) => query.state.data === undefined }),
    [queryClient],
  );

  useEffect(() => {
    if (!unreachable) return;
    const timer = setInterval(retry, RETRY_EVERY_MS);
    window.addEventListener("online", retry);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", retry);
    };
  }, [unreachable, retry]);

  if (!unreachable) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-3 safe-top">
      <div
        role="alert"
        className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-card border border-accent/50 bg-base/95 px-4 py-3 text-[0.9rem] shadow-lg"
      >
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-accent">Serveur injoignable</span>
          <span className="block text-[0.82rem] text-ink-soft">
            Tailscale est-il connecté ? Nouvel essai automatique.
          </span>
        </span>
        <button
          type="button"
          onClick={retry}
          className="h-10 shrink-0 rounded-card bg-accent px-4 font-bold text-accent-ink"
        >
          Réessayer
        </button>
      </div>
    </div>
  );
}
